/**
 * Business decision mapping (presentation layer).
 *
 * Prefers locked credit-committee conclusions from the backend when present.
 * Falls back to zone-based mapping for older payloads.
 */

const USE_CASE_LABELS = {
  vendor: 'Counterparty risk',
  subsidiary: 'Subsidiary monitoring',
  ma_private: 'M&A / target triage',
  investor: 'Investor'
};

/**
 * Discrete business decision for the analysis lens.
 * Tone: low | medium | high - same semantic palette as risk bands.
 */
export const businessDecision = (riskZone, useCase = 'vendor', aiAnalysis = {}) => {
  const lens = USE_CASE_LABELS[useCase] ? useCase : 'vendor';
  const committee = aiAnalysis.committeeDecision;

  if (lens === 'investor') {
    if (aiAnalysis.recommendation) {
      const rec = aiAnalysis.recommendation;
      const tone =
        rec === 'Buy' ? 'low' : rec === 'Hold' ? 'medium' : 'high';
      return {
        label: rec,
        detail: aiAnalysis.recommendationReasoning || 'Investment framing from analysis lens.',
        tone,
        category: 'Investment view'
      };
    }
    const byZone = {
      'Safe Zone': { label: 'Hold / Monitor', tone: 'low' },
      'Grey Zone': { label: 'Hold / elevated watch', tone: 'medium' },
      'Distress Zone': { label: 'Reduce / Avoid', tone: 'high' }
    };
    const d = byZone[riskZone] || { label: 'Review required', tone: 'medium' };
    return { ...d, detail: 'Mapped from risk band under investor lens.', category: 'Investment view' };
  }

  if (committee && committee.businessDecision) {
    return {
      label: committee.businessDecision,
      detail: committee.businessDetail || '',
      tone: committee.tone || 'medium',
      category: committee.category || 'Business decision'
    };
  }

  if (lens === 'subsidiary') {
    const byZone = {
      'Safe Zone': {
        label: 'Standard oversight',
        detail: 'Entity within normal risk band - maintain standard group reporting cadence.',
        tone: 'low'
      },
      'Grey Zone': {
        label: 'Enhanced monitoring',
        detail: 'Elevate management attention; confirm intercompany support and reporting alignment.',
        tone: 'medium'
      },
      'Distress Zone': {
        label: 'Board escalation',
        detail: 'Require remediation plan and board-level review before further capital commitments.',
        tone: 'high'
      }
    };
    return {
      ...(byZone[riskZone] || { label: 'Review required', detail: '', tone: 'medium' }),
      category: 'Governance decision'
    };
  }

  if (lens === 'ma_private') {
    const byZone = {
      'Safe Zone': {
        label: 'Proceed to diligence',
        detail: 'Financial screen does not block progression; continue with standard QoE and legal diligence.',
        tone: 'low'
      },
      'Grey Zone': {
        label: 'Conditional: targeted diligence first',
        detail: 'Resolve liquidity, leverage, or trajectory concerns before committing deal resources.',
        tone: 'medium'
      },
      'Distress Zone': {
        label: 'High caution: restructuring expertise required',
        detail: 'Do not proceed without distress/restructuring specialists and revised valuation assumptions.',
        tone: 'high'
      }
    };
    return {
      ...(byZone[riskZone] || { label: 'Review required', detail: '', tone: 'medium' }),
      category: 'Deal gate'
    };
  }

  // Default: vendor / counterparty procurement
  const byZone = {
    'Safe Zone': {
      label: 'Continue business',
      detail: 'Counterparty financial health supports normal commercial terms and relationship cadence.',
      tone: 'low'
    },
    'Grey Zone': {
      label: 'Continue with enhanced monitoring',
      detail: 'Maintain relationship; tighten review cadence and confirm contract early-warning rights.',
      tone: 'medium'
    },
    'Distress Zone': {
      label: 'Reduce exposure / do not onboard',
      detail: 'Limit new commitment; dual-source critical inputs; require financial review before renewal or onboarding.',
      tone: 'high'
    }
  };
  return {
    ...(byZone[riskZone] || { label: 'Review required', detail: '', tone: 'medium' }),
    category: 'Business decision'
  };
};

export const useCaseLabel = (useCase) => USE_CASE_LABELS[useCase] || USE_CASE_LABELS.vendor;

export const isInvestorLens = (useCase) => useCase === 'investor';

/**
 * Primary action for Now what.
 * Position is the single commercial truth (P1.6 Block 2) — never let committee
 * steering fight a composed Position (e.g. net-45 vs net-30).
 */
export const primaryAction = (riskZone, useCase, aiAnalysis = {}) => {
  if (aiAnalysis?.declined || aiAnalysis?.outcome === 'declined') {
    return { text: '', reasoning: '' };
  }

  if (aiAnalysis.position && String(aiAnalysis.position).trim().length >= 20) {
    return {
      text: aiAnalysis.position,
      reasoning: aiAnalysis.soWhat || aiAnalysis.steeringReasoning || ''
    };
  }

  if (isInvestorLens(useCase) && aiAnalysis.recommendation) {
    return {
      text: aiAnalysis.recommendation,
      reasoning: aiAnalysis.recommendationReasoning || ''
    };
  }

  if (aiAnalysis.steering && String(aiAnalysis.steering).trim().length >= 20) {
    return {
      text: aiAnalysis.steering,
      reasoning: aiAnalysis.soWhat || aiAnalysis.steeringReasoning || ''
    };
  }

  const committee = aiAnalysis.committeeDecision;
  if (committee?.recommendedAction) {
    return {
      text: committee.recommendedAction,
      reasoning: committee.actionReasoning || ''
    };
  }

  const decision = businessDecision(riskZone, useCase, aiAnalysis);
  return { text: decision.label, reasoning: decision.detail };
};

/** Monitoring cadence from committee engine, else zone fallback. */
export const monitoringCadence = (riskZone, aiAnalysis = {}) => {
  if (aiAnalysis.committeeDecision?.monitoringCadence) {
    return aiAnalysis.committeeDecision.monitoringCadence;
  }
  switch (riskZone) {
    case 'Safe Zone':
      return 'Semi-annual';
    case 'Grey Zone':
      return 'Quarterly';
    case 'Distress Zone':
      return 'Monthly';
    default:
      return 'Quarterly';
  }
};
